import { useMemo, useState } from 'react';
import {
  Plus,
  RefreshCw,
  Eye,
  Trash2,
  ArrowRightLeft,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Pagination } from '../ui/pagination';
import { LoadingTable } from '../ui/loading-states';
import { ErrorState } from '../ui/error-state';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  MedicationPicker,
  aggregateStockOptions,
  type StockOption,
} from './medication-picker';
import {
  useStockTransfers,
  useStockTransfer,
  useCreateStockTransfer,
} from '../../hooks/useStockTransfers';
import { useOneCStock } from '../../hooks/useOneCStock';
import { useWarehousesForSelect } from '../../hooks/useWarehouses';
import type {
  StockTransfer,
  CreateStockTransferItem,
} from '../../types/stock-transfers.types';

function formatDate(value?: string): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('ru-RU');
}

// ---------------------------------------------------------------------------
// Вкладка «Перемещения» — прямое перемещение между складами (создаётся в админке,
// синхронно уходит в 1С). См. docs/STOCK_TRANSFERS_INTEGRATION.md.
// ---------------------------------------------------------------------------

export function TransfersTab() {
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const filters = useMemo(() => ({ page, limit: 20 }), [page]);
  const { data, isLoading, error, refetch, isFetching } =
    useStockTransfers(filters);

  const rows = data?.data ?? [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Перемещения между складами</CardTitle>
            <CardDescription>
              Прямое перемещение медикаментов со склада-отправителя на
              склад-получатель. Документ сразу создаётся в 1С.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <RefreshCw
                className={`w-4 h-4 mr-2 ${isFetching ? 'animate-spin' : ''}`}
              />
              Обновить
            </Button>
            <Button size="sm" onClick={() => setShowCreate(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Новое перемещение
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <LoadingTable rows={6} columns={5} />
        ) : error ? (
          <ErrorState
            title="Не удалось загрузить перемещения"
            description={(error as Error)?.message}
            onAction={() => refetch()}
          />
        ) : rows.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            Перемещений пока нет
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Отправитель → Получатель</TableHead>
                  <TableHead>№ документа 1С</TableHead>
                  <TableHead className="text-right">Позиций</TableHead>
                  <TableHead>Создал</TableHead>
                  <TableHead>Создано</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer"
                    onClick={() => setDetailId(row.id)}
                  >
                    <TableCell className="font-medium">
                      {row.sourceWarehouseName ||
                        row.sourceWarehouseId.slice(0, 8)}
                      <ArrowRightLeft className="inline w-3.5 h-3.5 mx-1.5 text-muted-foreground" />
                      {row.destinationWarehouseName ||
                        row.destinationWarehouseId.slice(0, 8)}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {row.onecDocNumber || row.onecDocId || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {row.items?.length ?? 0}
                    </TableCell>
                    <TableCell className="text-sm">
                      {row.createdByEmployeeName || '—'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(row.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Eye className="w-4 h-4 text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="mt-4">
              <Pagination
                currentPage={data?.page ?? 1}
                totalPages={data?.totalPages ?? 1}
                onPageChange={setPage}
              />
            </div>
          </>
        )}
      </CardContent>

      {showCreate && (
        <CreateTransferDialog onClose={() => setShowCreate(false)} />
      )}
      <TransferDetailDialog id={detailId} onClose={() => setDetailId(null)} />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Диалог создания перемещения
// ---------------------------------------------------------------------------

interface ItemRow {
  medicationName: string;
  medicationCode1C: string;
  quantity: string;
  uom: string;
  /** Доступный остаток выбранного медикамента на складе-отправителе (0 — не выбран). */
  available: number;
}

const emptyRow = (): ItemRow => ({
  medicationName: '',
  medicationCode1C: '',
  quantity: '',
  uom: 'упаковка',
  available: 0,
});

function CreateTransferDialog({ onClose }: { onClose: () => void }) {
  const createMutation = useCreateStockTransfer();
  const { data: warehouses } = useWarehousesForSelect();

  const [sourceWarehouseId, setSourceWarehouseId] = useState('');
  const [destinationWarehouseId, setDestinationWarehouseId] = useState('');
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState<ItemRow[]>([emptyRow()]);

  // Остатки склада-ОТПРАВИТЕЛЯ из 1С — единственный источник позиций.
  const {
    data: stock,
    isLoading: stockLoading,
    isError: stockError,
  } = useOneCStock(sourceWarehouseId || undefined);

  const stockOptions = useMemo<StockOption[]>(
    () => aggregateStockOptions(stock?.items),
    [stock],
  );

  const updateRow = (idx: number, patch: Partial<ItemRow>) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (idx: number) =>
    setRows((prev) => prev.filter((_, i) => i !== idx));

  // Смена склада-отправителя обнуляет позиции — прежние остатки уже неактуальны.
  const handleSourceChange = (id: string) => {
    setSourceWarehouseId(id);
    setRows([emptyRow()]);
  };

  const selectMedication = (idx: number, opt: StockOption) =>
    updateRow(idx, {
      medicationName: opt.name,
      medicationCode1C: opt.code,
      available: opt.available,
    });

  const chosenCodes = useMemo(
    () => rows.map((r) => r.medicationCode1C).filter(Boolean),
    [rows],
  );

  const rowError = (r: ItemRow): string | null => {
    if (!r.medicationName || !r.quantity) return null;
    const qty = Number(r.quantity);
    if (!Number.isFinite(qty) || qty <= 0) return 'Укажите количество';
    if (qty > r.available) return `Больше остатка (доступно ${r.available})`;
    return null;
  };
  const hasRowErrors = rows.some((r) => rowError(r) !== null);

  const validItems: CreateStockTransferItem[] = rows
    .filter((r) => {
      const qty = Number(r.quantity);
      return (
        !!r.medicationName &&
        r.available > 0 &&
        Number.isFinite(qty) &&
        qty > 0 &&
        qty <= r.available
      );
    })
    .map((r) => ({
      medicationCode1C: r.medicationCode1C.trim() || undefined,
      medicationName: r.medicationName.trim(),
      quantity: Number(r.quantity),
      uom: r.uom.trim() || undefined,
    }));

  const sameWarehouse =
    !!sourceWarehouseId && sourceWarehouseId === destinationWarehouseId;

  const canSubmit =
    !!sourceWarehouseId &&
    !!destinationWarehouseId &&
    !sameWarehouse &&
    validItems.length > 0 &&
    !hasRowErrors;

  const handleCreate = async () => {
    if (!canSubmit) return;
    // При ошибке 1С мутация бросит — диалог НЕ закрываем, показываем текст ниже.
    await createMutation.mutateAsync({
      sourceWarehouseId,
      destinationWarehouseId,
      notes: notes.trim() || undefined,
      items: validItems,
    });
    onClose();
  };

  const stockReady =
    !!sourceWarehouseId &&
    !stockLoading &&
    !stockError &&
    stockOptions.length > 0;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Новое перемещение</DialogTitle>
          <DialogDescription>
            Документ сразу создаётся в 1С. Позиции подбираются из остатков
            склада-отправителя.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Склад-отправитель</Label>
            <Select value={sourceWarehouseId} onValueChange={handleSourceChange}>
              <SelectTrigger>
                <SelectValue placeholder="Выберите склад" />
              </SelectTrigger>
              <SelectContent>
                {(warehouses ?? []).map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Склад-получатель</Label>
            <Select
              value={destinationWarehouseId}
              onValueChange={setDestinationWarehouseId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Выберите склад" />
              </SelectTrigger>
              <SelectContent>
                {(warehouses ?? []).map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {sameWarehouse && (
              <span className="text-xs text-destructive">
                Склады должны отличаться
              </span>
            )}
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Заметка (необязательно)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Например: плановое пополнение"
              rows={2}
            />
          </div>
        </div>

        {/* Позиции */}
        <div className="mt-2">
          <div className="flex items-center justify-between mb-2">
            <Label>Позиции</Label>
            <Button
              variant="outline"
              size="sm"
              onClick={addRow}
              disabled={!stockReady}
            >
              <Plus className="w-4 h-4 mr-1" />
              Добавить
            </Button>
          </div>

          {!sourceWarehouseId ? (
            <p className="text-sm text-muted-foreground py-4 text-center border rounded-md">
              Сначала выберите склад-отправитель — позиции подбираются из его
              остатков в 1С.
            </p>
          ) : stockLoading ? (
            <p className="text-sm text-muted-foreground py-4 text-center border rounded-md">
              Загрузка остатков склада…
            </p>
          ) : stockError ? (
            <p className="text-sm text-destructive py-4 text-center border rounded-md">
              Не удалось загрузить остатки склада. Проверьте настройку
              интеграции с 1С и код 1С у склада.
            </p>
          ) : stockOptions.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center border rounded-md">
              На выбранном складе нет остатков.
            </p>
          ) : (
            <div className="min-h-[360px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Медикамент (из остатков склада)</TableHead>
                    <TableHead className="w-28 text-right">Кол-во</TableHead>
                    <TableHead className="w-28">Ед.</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r, idx) => {
                    const err = rowError(r);
                    const rowOptions = stockOptions.filter(
                      (o) =>
                        o.code === r.medicationCode1C ||
                        !chosenCodes.includes(o.code),
                    );
                    return (
                      <TableRow key={idx}>
                        <TableCell className="align-top">
                          <MedicationPicker
                            options={rowOptions}
                            selectedName={r.medicationName}
                            onSelect={(opt) => selectMedication(idx, opt)}
                          />
                          {r.available > 0 && (
                            <span className="text-xs text-muted-foreground">
                              Доступно: {r.available}
                              {r.medicationCode1C
                                ? ` · код 1С: ${r.medicationCode1C}`
                                : ''}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="align-top">
                          <Input
                            type="number"
                            min={1}
                            max={r.available || undefined}
                            value={r.quantity}
                            disabled={r.available === 0}
                            onChange={(e) =>
                              updateRow(idx, { quantity: e.target.value })
                            }
                            className={`text-right ${err ? 'border-destructive' : ''}`}
                          />
                          {err && (
                            <span className="text-xs text-destructive">
                              {err}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="align-top">
                          <Input
                            value={r.uom}
                            onChange={(e) =>
                              updateRow(idx, { uom: e.target.value })
                            }
                          />
                        </TableCell>
                        <TableCell className="align-top">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeRow(idx)}
                            disabled={rows.length === 1}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {createMutation.isError && (
          <p className="text-sm text-destructive">
            {(createMutation.error as Error)?.message ||
              'Не удалось создать перемещение'}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Отмена
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!canSubmit || createMutation.isPending}
          >
            {createMutation.isPending
              ? 'Создание в 1С…'
              : 'Создать перемещение'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Диалог деталей перемещения (read-only)
// ---------------------------------------------------------------------------

function TransferDetailDialog({
  id,
  onClose,
}: {
  id: string | null;
  onClose: () => void;
}) {
  const { data, isLoading, error, refetch } = useStockTransfer(id);

  return (
    <Dialog open={!!id} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Перемещение</DialogTitle>
          {data && (
            <DialogDescription>
              {data.sourceWarehouseName || data.sourceWarehouseId}
              {' → '}
              {data.destinationWarehouseName || data.destinationWarehouseId}
            </DialogDescription>
          )}
        </DialogHeader>

        {isLoading ? (
          <LoadingTable rows={4} columns={3} />
        ) : error ? (
          <ErrorState
            title="Не удалось загрузить перемещение"
            description={(error as Error)?.message}
            onAction={() => refetch()}
          />
        ) : data ? (
          <TransferDetailBody transfer={data} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function TransferDetailBody({ transfer }: { transfer: StockTransfer }) {
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
        <span>Создано: {formatDate(transfer.createdAt)}</span>
        {transfer.createdByEmployeeName && (
          <span>· {transfer.createdByEmployeeName}</span>
        )}
      </div>

      {transfer.notes && (
        <p className="text-sm">
          <span className="text-muted-foreground">Заметка: </span>
          {transfer.notes}
        </p>
      )}

      <div>
        <h3 className="text-sm font-medium mb-2">Позиции</h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Наименование</TableHead>
              <TableHead>Код 1С</TableHead>
              <TableHead className="text-right">Кол-во</TableHead>
              <TableHead>Ед.</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {transfer.items.map((it) => (
              <TableRow key={it.id || it.lineNumber}>
                <TableCell>{it.medicationName || '—'}</TableCell>
                <TableCell className="font-mono text-xs">
                  {it.medicationCode1C || '—'}
                </TableCell>
                <TableCell className="text-right">{it.quantity}</TableCell>
                <TableCell>{it.uom || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Документ 1С</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
          <InfoLine label="Статус синхр." value={transfer.onecSyncStatus} />
          <InfoLine
            label="№ документа"
            value={transfer.onecDocNumber}
            mono
          />
          <InfoLine label="UID документа" value={transfer.onecDocId} mono />
          <InfoLine
            label="Синхронизировано"
            value={
              transfer.onecSyncedAt ? formatDate(transfer.onecSyncedAt) : null
            }
          />
          {transfer.onecSyncError && (
            <div className="col-span-2 text-destructive">
              Ошибка 1С: {transfer.onecSyncError}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function InfoLine({
  label,
  value,
  mono,
}: {
  label: string;
  value?: string | null;
  mono?: boolean;
}) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className={mono ? 'font-mono text-xs' : ''}>{value || '—'}</span>
    </div>
  );
}
